import React, { useEffect, useState, useContext } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { FaCar, FaShieldAlt, FaUsers } from 'react-icons/fa';
import { AuthContext } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import Alert from '../common/Alert';
import FormInput from '../common/FormInput';
import Button from '../common/Button';
import useApi from '../../hooks/useApi';

function Login() {
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { login, session } = useContext(AuthContext);
  const { post } = useApi();
  const { loading } = useContext(AuthContext);
  const { get } = useApi();

  useEffect(() => {
    const accessMessage = sessionStorage.getItem('loginMessage');
    if (accessMessage) {
      setError(accessMessage);
      sessionStorage.removeItem('loginMessage');
    }
    const checkSession = async () => {
      const token = localStorage.getItem('token');
      const role = localStorage.getItem('role');
      const username = localStorage.getItem('username');
      if (token && role && username) {
        try {
          await get('/api/users/current');
          if (role.includes('ADMIN') || role.includes('SUPER_ADMIN')) {
            navigate('/dashboard', { replace: true });
          } else if (role.includes('SECURITY')) {
            navigate('/dashboard', { replace: true });
          } else {
            navigate('/dashboard', { replace: true });
          }
          toast.info(`Welcome back, ${username}!`);
        } catch (err) {
          localStorage.clear();
          toast.error('Session expired.');
        }
      }
    };
    if (!loading) {
      checkSession();
    }
  }, [navigate, loading, get]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    try {
      const existingToken = localStorage.getItem('token');
      const existingUsername = localStorage.getItem('username');
      if (existingToken && existingUsername && existingUsername !== formData.username) {
        if (
          !window.confirm(
            `User "${existingUsername}" is logged in. Continue as "${formData.username}"?`
          )
        ) {
          return;
        }
      }

      ['token','role','username','userId','communityId','communityName','communityDisplayName'].forEach(key => localStorage.removeItem(key));

      const data = await post('/api/auth/login', formData);
      const { token, role } = data;
      if (!token || !role) throw new Error('Invalid response from server');

      const roles = Array.isArray(role) ? role : [role];
      await login(token, roles.join(','), formData.username);
      toast.success(`Welcome, ${formData.username}!`);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      const message = err.message || 'Invalid username or password.';
      setError(message);
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="auth-page">
      <section className="auth-story">
        <span className="auth-eyebrow">Welcome home</span>
        <h1>Your community is only a sign-in away.</h1>
        <p>Stay informed, manage your household and help keep every arrival secure.</p>
        <div className="auth-benefits">
          <span><FaCar /><span><strong>Vehicles</strong><small>Resident and guest access</small></span></span>
          <span><FaUsers /><span><strong>People</strong><small>Family and community directory</small></span></span>
          <span><FaShieldAlt /><span><strong>Security</strong><small>Clear, role-based visibility</small></span></span>
        </div>
      </section>
      <section className="auth-panel">
          <div className="card auth-card">
            <div className="card-body">
              <span className="auth-form-kicker">Resident access</span>
              <h3 className="card-title">Sign in to MyCommunity</h3>
              <p className="auth-form-copy">Enter the credentials registered with your community.</p>
              {error && <Alert type="danger" message={error} />}
              <form onSubmit={handleSubmit}>
                <FormInput
                  label="Username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  required
                />
                <FormInput
                  label="Password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                />
                <Button type="submit" variant="primary" className="w-100" disabled={isSubmitting}>
                  {isSubmitting ? 'Signing in…' : 'Sign in securely'}
                </Button>
              </form>
              <p className="auth-switch"><NavLink to="/forgot-password">Forgot your password?</NavLink></p>
              <p className="auth-switch">
                New to MyCommunity? <NavLink to="/signup">Create an account</NavLink>
              </p>
            </div>
          </div>
      </section>
    </div>
  );
}

export default Login;
